import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import '@/styles/tactical-ui.css';
import usePageTitle from "@/hooks/usePageTitle";

const ForgotPassword = () => {
  usePageTitle("pageTitles.forgotPassword");
  const { supabase } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-amber-500 mb-2">Check Your Email</h1>
            <p className="text-slate-400 text-lg">
              We've sent a password reset link to:
            </p>
            <p className="text-white font-semibold mt-2">{email}</p>
          </div>

          <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 mb-6">
            <p className="text-slate-300 text-sm">
              <strong className="text-white">What's next?</strong>
              <br />
              1. Check your email inbox (and spam folder)
              <br />
              2. Click the "Reset Password" link in the email
              <br />
              3. You'll be redirected to create a new password
            </p>
          </div>

          <div className="text-center">
            <Link 
              to="/login" 
              className="text-amber-500 hover:text-amber-400 font-semibold transition-colors"
            >
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-500 mb-2">Reset Password</h1>
          <p className="text-slate-400 text-lg">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-slate-300 mb-2 font-medium">Email Address</label>
            <input
              type="email"
              className="auth-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="chef@restaurant.com"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-400">
            Remember your password?{' '}
            <Link to="/login" className="text-amber-500 hover:text-amber-400 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
